import { useAuth as useAuthContext } from '../contexts/AuthContext';
import { AuthContextType, UserRole, Permission } from '../schemas/auth';

// Custom hook for authentication
export const useAuth = (): AuthContextType => {
  const auth = useAuthContext();
  return auth;
};

// Hook for protected routes
export const useRequireAuth = (): AuthContextType => {
  const auth = useAuthContext();

  if (!auth.isAuthenticated && !auth.isLoading) {
    // Redirect to login or throw error
    throw new Error('Authentication required');
  }

  return auth;
};

// Hook for role-based access
export const useRequireRole = (requiredRoles: UserRole[]): AuthContextType => {
  const auth = useAuthContext();

  if (!auth.isAuthenticated) {
    throw new Error('Authentication required');
  }

  if (requiredRoles && !requiredRoles.includes(auth.user?.role as UserRole)) {
    throw new Error(`Access denied. Required role: ${requiredRoles.join(' or ')}`);
  }

  return auth;
};

// Hook for permission-based access
export const useRequirePermission = (
  requiredPermissions: Permission | Permission[]
): AuthContextType => {
  const auth = useAuthContext();

  if (!auth.isAuthenticated) {
    throw new Error('Authentication required');
  }

  const hasPermission = Array.isArray(requiredPermissions)
    ? auth.hasAllPermissions(requiredPermissions)
    : auth.hasPermission(requiredPermissions);

  if (!hasPermission) {
    throw new Error(`Access denied. Required permission: ${requiredPermissions}`);
  }

  return auth;
};

export default useAuth;
